import { useEffect, useState, type ButtonHTMLAttributes, type PropsWithChildren, type ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Link } from "react-router-dom";
import { trackEvent } from "../lib/analytics";

export function SectionLabel({ children, inverse = false }: PropsWithChildren<{ inverse?: boolean }>) {
  return (
    <div className={`section-label${inverse ? " section-label--inverse" : ""}`}>
      <span aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}

export function SystemSignal({ light = false }: { light?: boolean }) {
  return (
    <div className={`system-signal${light ? " system-signal--light" : ""}`} aria-label="راهبرد، داده، هوش مصنوعی و اجرا">
      <span>استراتژی</span><b aria-hidden="true">•</b><span>داده</span><b aria-hidden="true">•</b><span>هوش مصنوعی</span><b aria-hidden="true">•</b><span>اجرا</span>
    </div>
  );
}

type SplitButtonProps = PropsWithChildren<{
  to?: string;
  className?: string;
  variant?: "dark" | "copper" | "outline";
  tracking?: { label: string; location: string; destination?: string };
}> & ButtonHTMLAttributes<HTMLButtonElement>;

export function SplitButton({ children, to, className = "", variant = "dark", tracking, ...buttonProps }: SplitButtonProps) {
  const content = (
    <>
      <span className="split-button__icon"><ArrowLeft size={20} strokeWidth={2.2} /></span>
      <span className="split-button__label">{children}</span>
    </>
  );
  const classes = `split-button split-button--${variant} ${className}`.trim();

  if (to) return (
    <Link
      className={classes}
      to={to}
      onClick={() => tracking && trackEvent("cta_click", {
        label: tracking.label,
        location: tracking.location,
        destination: tracking.destination ?? to,
      })}
    >
      {content}
    </Link>
  );
  return <button className={classes} {...buttonProps}>{content}</button>;
}

export function Reveal({ children, className = "", delay = 0, as = "div" }: PropsWithChildren<{ className?: string; delay?: number; as?: "div" | "section" }>) {
  const reduce = useReducedMotion();
  const isDesignCapture = typeof window !== "undefined" && window.location.hash.includes("figmacapture=");
  const staticRender = reduce || isDesignCapture;
  const Component = as === "section" ? motion.section : motion.div;
  return (
    <Component
      className={className}
      initial={staticRender ? false : { y: 24 }}
      whileInView={staticRender ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.58, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </Component>
  );
}

export function DecisionTrace({ vertical = false, active = 2 }: { vertical?: boolean; active?: number }) {
  return (
    <div className={`decision-trace${vertical ? " decision-trace--vertical" : ""}`} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((item) => <span className={item === active ? "is-active" : ""} key={item} />)}
    </div>
  );
}

function useDesktopViewport() {
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const query = window.matchMedia("(min-width: 721px)");
    const update = () => setIsDesktop(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return isDesktop;
}

export function DesktopHeroMedia({ image, className }: { image: string; className: string }) {
  const isDesktop = useDesktopViewport();

  if (!isDesktop) return null;

  return (
    <picture className={className}>
      <img src={image} alt="" width="1600" height="900" fetchPriority="high" decoding="async" />
    </picture>
  );
}

export function MobileHeroPreview({ image, className }: { image: string; className: string }) {
  const isDesktop = useDesktopViewport();

  if (isDesktop) return null;

  return (
    <picture className={className} aria-hidden="true">
      <img src={image} alt="" width="1600" height="900" fetchPriority="high" decoding="async" />
    </picture>
  );
}

export function PageHero({
  desktopImage,
  mobileArtwork,
  label,
  title,
  body,
  dark = false,
  children,
  flipImage = false,
}: {
  desktopImage: string;
  mobileArtwork: string;
  label: string;
  title: ReactNode;
  body: string;
  dark?: boolean;
  children?: ReactNode;
  flipImage?: boolean;
}) {
  return (
    <section className={`page-hero${dark ? " page-hero--dark" : ""}`}>
      <DesktopHeroMedia image={desktopImage} className={`page-hero__media${flipImage ? " page-hero__media--flip" : ""}`} />
      <div className="page-hero__scrim" aria-hidden="true" />
      <Reveal className="page-hero__copy">
        <SectionLabel inverse={dark}>{label}</SectionLabel>
        <h1>{title}</h1>
        <p>{body}</p>
        {children}
        <MobileHeroPreview image={mobileArtwork} className="page-hero__mobile-preview" />
      </Reveal>
      <DecisionTrace active={3} />
    </section>
  );
}
